import type { ViewerControls } from './mocked-viewer-controls/mocked-viewer-controls';
import type { ViewerState } from './mocked-viewer-state/mocked-viewer-state';
import type { ChatType, State } from '../generated/graphql-viewer';

import { gql, useApolloClient } from '@apollo/client';
import { type ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router';

import { ENV } from '../config/env';
import { pathsWithoutChannelId } from '../routes';
import { MockedViewerChat } from './mocked-viewer-chat/mocked-viewer-chat';
import { MockedViewerControls } from './mocked-viewer-controls/mocked-viewer-controls';
import { MockedEventState } from './mocked-viewer-state/mocked-event-state';
import { MockedViewerState } from './mocked-viewer-state/mocked-viewer-state';
import { FeatureToggle } from '../service/feature-toggle/feature-toggle';

import styles from './dev-tools.module.css';

type DevToolsContextValue = {
  mockedEventState?: State;
  mockedViewerState?: ViewerState;
  mockedViewerChat?: ChatType;
  mockedViewerChatNamesVisible?: boolean;
  mockedViewerControls: ViewerControls;
};

const initialDevToolsContext: DevToolsContextValue = {
  mockedEventState: undefined,
  mockedViewerState: undefined,
  mockedViewerChat: undefined,
  mockedViewerChatNamesVisible: undefined,
  mockedViewerControls: {
    headerButtonsVisible: false,
    mockedSlide: undefined,
    speakerHighlighted: false,
  },
};

export const DevToolsContext = createContext<DevToolsContextValue>(initialDevToolsContext);

export function DevToolsProvider({ children }: { children: ReactNode }) {
  const { webcastId = '' } = useParams();
  const { pathname } = useLocation();

  const [menuVisible, setMenuVisible] = useState(false);
  const [mockedEventState, setMockedEventState] = useState(initialDevToolsContext.mockedEventState);
  const [mockedViewerState, setMockedViewerState] = useState(initialDevToolsContext.mockedViewerState);
  const [mockedViewerChat, setMockedViewerChat] = useState(initialDevToolsContext.mockedViewerChat);
  const [mockedViewerChatNamesVisible, setMockedViewerChatNamesVisible] = useState(
    initialDevToolsContext.mockedViewerChatNamesVisible
  );
  const [mockedViewerControls, setMockedViewerControls] = useState(initialDevToolsContext.mockedViewerControls);

  const isDevelopment = ENV.MODE === 'development';
  const isIframePage = pathname.includes(pathsWithoutChannelId.iFrame);

  const client = useApolloClient();

  useEffect(() => {
    if (mockedEventState && webcastId) {
      client.writeFragment({
        id: client.cache.identify({ __typename: 'Webcast', id: webcastId }),
        fragment: gql`
          fragment MockedState on Webcast {
            state
          }
        `,
        data: { state: mockedEventState },
      });
    }
  }, [client, mockedEventState, webcastId]);

  if (!isDevelopment || isIframePage) return <>{children}</>;

  return (
    <DevToolsContext.Provider
      value={{ mockedViewerState, mockedViewerChat, mockedViewerChatNamesVisible, mockedViewerControls }}
    >
      {children}

      <FeatureToggle featureName="devTools">
        <div className={styles.wrapper}>
          {menuVisible && (
            <div className={styles.menus}>
              <MockedEventState value={mockedEventState} onChange={setMockedEventState} />

              <MockedViewerState value={mockedViewerState} onChange={setMockedViewerState} />

              <MockedViewerChat
                chatType={mockedViewerChat}
                namesVisible={mockedViewerChatNamesVisible}
                onTypeChange={setMockedViewerChat}
                onNameVisibilityChange={setMockedViewerChatNamesVisible}
              />

              <MockedViewerControls
                value={mockedViewerControls}
                onChange={(name, value) => setMockedViewerControls({ ...mockedViewerControls, [name]: value })}
              />
            </div>
          )}

          <button className={styles.button} onClick={() => setMenuVisible((state) => !state)}>
            ☰
          </button>
        </div>
      </FeatureToggle>
    </DevToolsContext.Provider>
  );
}

export const useDevTools = () => useContext(DevToolsContext);
